import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  FolderIcon, 
  InboxIcon, 
  ArchiveBoxIcon, 
  PlusIcon 
} from '@heroicons/react/24/outline';
import { userService } from '../services/UserService';

function FolderSelectionModal({ isOpen, onClose, onSelectFolder, currentFolderId }) {
  const { t } = useTranslation();
  const [folders, setFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const updateFolders = (userData) => {
      if (userData && userData.folders) {
        setFolders(userData.folders);
      } else {
        setFolders([]);
      }
    };

    updateFolders(userService.currentUser);
    const unsubscribe = userService.subscribe(updateFolders);
    return unsubscribe;
  }, []);

  if (!isOpen) return null;

  const systemFolders = [
    { id: 'inbox', name: t('notes.inbox'), icon: InboxIcon },
    { id: 'archived', name: t('notes.archive'), icon: ArchiveBoxIcon },
  ];

  const filteredFolders = folders.filter(folder => 
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (folderId) => {
    onSelectFolder(folderId);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60] flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('modals.moveTo')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Search (Optional, but good for many folders) */}
        <div className="p-2 border-b border-gray-100">
            <input
                type="text"
                placeholder={t('modals.searchFolders')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>

        {/* List */}
        <div className="overflow-y-auto max-h-[60vh]">
            {/* System Folders */}
            <div className="py-2">
                {systemFolders.map(folder => (
                    <button
                        key={folder.id}
                        onClick={() => handleSelect(folder.id)}
                        className={`w-full flex items-center px-4 py-3 hover:bg-gray-50 transition-colors ${currentFolderId === folder.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                    >
                        <folder.icon className={`h-5 w-5 mr-3 ${currentFolderId === folder.id ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="flex-1 text-left">{folder.name}</span>
                        {currentFolderId === folder.id && (
                            <span className="text-blue-600 text-sm">{t('modals.current')}</span>
                        )}
                    </button>
                ))}
            </div>

            <div className="border-t border-gray-100 my-1"></div>

            {/* User Folders */}
            <div className="py-2">
                <div className="px-4 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('modals.myFolders')}
                </div>
                {filteredFolders.length > 0 ? (
                    filteredFolders.map(folder => (
                        <button
                            key={folder.id}
                            onClick={() => handleSelect(folder.id)}
                            className={`w-full flex items-center px-4 py-3 hover:bg-gray-50 transition-colors ${currentFolderId === folder.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                        >
                            <FolderIcon className={`h-5 w-5 mr-3 ${currentFolderId === folder.id ? 'text-blue-600' : 'text-gray-400'}`} />
                            <span className="flex-1 text-left">{folder.name}</span>
                            {currentFolderId === folder.id && (
                                <span className="text-blue-600 text-sm">{t('modals.current')}</span>
                            )}
                        </button>
                    ))
                ) : (
                    <div className="px-4 py-2 text-sm text-gray-400 italic">
                        {searchTerm ? t('modals.noFoldersFound') : t('modals.noCustomFolders')}
                    </div>
                )}
            </div>
            
            {/* Add Folder (Placeholder for now as per plan "optional") */}
             <button
                className="w-full flex items-center px-4 py-3 text-orange-600 hover:bg-orange-50 transition-colors border-t border-gray-100"
                onClick={() => {
                    // Future implementation: Open create folder dialog
                    console.log('Create new folder clicked');
                }}
            >
                <PlusIcon className="h-5 w-5 mr-3" />
                <span className="font-medium">{t('modals.createNewFolder')}</span>
            </button>
        </div>
      </div>
    </div>
  );
}

FolderSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectFolder: PropTypes.func.isRequired,
  currentFolderId: PropTypes.string,
};

export default FolderSelectionModal;

